import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { providersService } from '../services/providersService';
import { Provider, UserRole, ProviderType } from '../types';
import { toast } from 'react-toastify';
import { RootState } from '../store/store';
import { ProvidersDetailModal } from '../components/ProvidersDetailModal';

const ProvidersPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.auth.user);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterActive, setFilterActive] = useState<string>('all');

  // Detail modal state
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  const canManageProviders = user?.role === UserRole.ORG_ADMIN ||
                             user?.role === UserRole.PLATFORM_ADMIN;

  const canViewProviders = user?.role === UserRole.MANAGER ||
                           user?.role === UserRole.ORG_ADMIN ||
                           user?.role === UserRole.PLATFORM_ADMIN ||
                           user?.role === UserRole.STAFF;

  const loadProviders = async () => {
    try {
      setLoading(true);
      const isActiveFilter =
        filterActive === 'all'
          ? undefined
          : filterActive === 'active';

      const data = await providersService.getAll(isActiveFilter);
      setProviders(data);
    } catch (error: any) {
      console.error('Failed to load providers:', error);
      toast.error(t('providers.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterActive]);

  const handleRowClick = (provider: Provider) => {
    setSelectedProvider(provider);
    setIsDetailModalOpen(true);
  };

  const handleEdit = () => {
    if (selectedProvider) {
      navigate(`/app/providers/${selectedProvider.id}`);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-xl text-gray-600 dark:text-gray-400">{t('common.loading')}</div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-8">
      <div className="mb-6 sm:mb-8">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-4 sm:mb-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
            {t('providers.title')}
          </h1>
          {canManageProviders && (
            <button
              onClick={() => navigate('/app/providers/new')}
              className="w-full sm:w-auto px-4 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 active:bg-blue-800 transition-colors min-h-[44px] font-medium"
            >
              + {t('providers.addProvider')}
            </button>
          )}
        </div>

        <div className="flex gap-4 mb-4 sm:mb-6">
          <div className="flex-1 sm:flex-none">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t('common.status')}
            </label>
            <select
              value={filterActive}
              onChange={(e) => setFilterActive(e.target.value)}
              className="w-full sm:w-auto px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white dark:bg-gray-700 dark:text-white min-h-[44px]"
            >
              <option value="all">{t('common.all')}</option>
              <option value="active">{t('common.active')}</option>
              <option value="inactive">{t('common.inactive')}</option>
            </select>
          </div>
        </div>
      </div>

      {providers.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg">
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {t('providers.noProviders')}
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
            {t('providers.providerHint')}
          </p>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200 dark:divide-gray-700">
            {providers.map((provider) => (
              <li key={provider.id}>
                <div
                  onClick={() => handleRowClick(provider)}
                  className="px-4 py-4 sm:px-6 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                >
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2 mb-2">
                        <h3 className="text-base sm:text-lg font-medium text-blue-600 dark:text-blue-400 hover:underline">
                          {provider.name}
                        </h3>
                        {provider.providerType === 'whatsapp' && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                            WhatsApp
                          </span>
                        )}
                        {!provider.isActive && (
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                            {t('common.inactive')}
                          </span>
                        )}
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 text-sm text-gray-600 dark:text-gray-400">
                        {provider.contactEmail && (
                          <div className="truncate">
                            <span className="font-medium">{t('providers.contactEmail')}:</span>{' '}
                            {provider.contactEmail}
                          </div>
                        )}
                        {provider.contactPhone && (
                          <div className="truncate">
                            <span className="font-medium">{t('providers.contactPhone')}:</span>{' '}
                            {provider.contactPhone}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {provider.providerType !== ProviderType.WHATSAPP && (
                        <button
                          onClick={(e) => { e.stopPropagation(); navigate(`/app/providers/${provider.id}/users`); }}
                          className="inline-flex items-center gap-2 px-4 py-2.5 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 active:bg-orange-700 transition-colors min-h-[44px] font-semibold shadow-sm"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                          </svg>
                          {t('users.title')}
                        </button>
                      )}
                      <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Detail Modal */}
      <ProvidersDetailModal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        provider={selectedProvider}
        onEdit={handleEdit}
        canManage={canManageProviders}
      />
    </div>
  );
};

export default ProvidersPage;
